import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Divider, FormControlLabel, Grid, MenuItem, Switch, TextField } from "@mui/material";
import WebIcon from "@mui/icons-material/Web";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import BrandAssetFields from "../components/BrandAssetFields";
import { CURRENCIES, LOCALES } from "../utils/options";
import { withoutDomainSettings } from "../utils/api";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";

const PAGE_DEFAULTS = {
  catalogue_eyebrow: "The catalogue",
  catalogue_title: "Journeys with room to breathe.",
  catalogue_copy: "Browse the operator's published journeys.",
};

const ASSET_KEYS = ["logo_url", "favicon_url", "analytics_id", "checkout_enabled", "stripe_enabled"];

// Real estate sites: the property home page reads its wording from
// site_editor.home and the about text and contact line from site_editor.footer
// (storefront/src/components/property). Blank keeps the built-in wording.
function propertyCopyFromSettings(settings) {
  const home = settings.site_editor?.home || {};
  const footer = settings.site_editor?.footer || {};
  return {
    eyebrow: home.eyebrow || "",
    title: home.title || "",
    copy: home.copy || "",
    description: footer.description || "",
    contact_line: footer.contact_line || footer.contactLine || "",
  };
}

function formFromSite(site) {
  const settings = site.settings || {};
  return {
    name: site.name || "",
    brand_name: site.brand_name || "",
    tagline: site.tagline || "",
    contact_email: site.contact_email || "",
    default_locale: site.default_locale || "en-AU",
    default_currency: site.default_currency || "AUD",
    assets: Object.fromEntries(ASSET_KEYS.map((key) => [key, settings[key] ?? (key.endsWith("enabled") ? false : "")])),
    pages: { ...PAGE_DEFAULTS, ...(settings.site_editor?.pages || {}) },
    property: propertyCopyFromSettings(settings),
    // The voice assistant on this website (SEEP AI); absent means on.
    seep_enabled: settings.seep?.enabled !== false,
  };
}

function ContentTab({ site, saving, patch, onOpenBuilder, onError, realEstate }) {
  const { isModuleEnabled } = useModuleEntitlements();
  const hasSeep = Boolean(isModuleEnabled?.("SEEP_AI"));
  const initial = useMemo(() => formFromSite(site), [site]);
  const [form, setForm] = useState(initial);
  // Reset the form only when a new revision arrives, not on every refetch.
  const revisionKey = `${site.id}:${site.revision}`;
  const [formKey, setFormKey] = useState(revisionKey);
  if (formKey !== revisionKey) {
    setFormKey(revisionKey);
    setForm(initial);
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const setPage = (field) => (event) => setForm((current) => ({ ...current, pages: { ...current.pages, [field]: event.target.value } }));
  const setCopy = (field) => (event) => setForm((current) => ({ ...current, property: { ...current.property, [field]: event.target.value } }));

  const editorSettings = (settings) => {
    const editor = settings.site_editor || {};
    if (!realEstate) return { ...editor, pages: form.pages };
    const copy = form.property;
    return {
      ...editor,
      home: { ...(editor.home || {}), eyebrow: copy.eyebrow.trim(), title: copy.title.trim(), copy: copy.copy.trim() },
      footer: { ...(editor.footer || {}), description: copy.description.trim(), contact_line: copy.contact_line.trim() },
    };
  };

  const save = () => {
    const settings = withoutDomainSettings(site.settings);
    patch({
      name: form.name.trim(),
      brand_name: form.brand_name.trim() || null,
      tagline: form.tagline.trim() || null,
      contact_email: form.contact_email.trim() || null,
      default_locale: form.default_locale,
      default_currency: form.default_currency,
      settings: {
        ...settings,
        ...form.assets,
        site_editor: editorSettings(settings),
        ...(hasSeep ? { seep: { ...(settings.seep || {}), enabled: form.seep_enabled } } : {}),
      },
    }, "Content saved");
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}`, bgcolor: colors.background.offWhite }}>
          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <WebIcon sx={{ color: colors.brand.primary, fontSize: 36 }} />
            <MDBox flex={1} minWidth={220}>
              <MDTypography variant="h6">Edit pages in the storefront builder</MDTypography>
              <MDTypography variant="body2" color="text">
                {realEstate
                  ? "Add your own pages, such as About us or Our team, in the builder. The home, buy, rent and property pages are built from your published listings; publishing a home page in the builder would replace yours."
                  : "Headlines, images, navigation, footer and every home page section are edited visually in the builder."}
              </MDTypography>
            </MDBox>
            <MDButton color="info" onClick={onOpenBuilder}>Open storefront builder</MDButton>
          </MDBox>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
          <MDTypography variant="h6" mb={2}>Brand and details</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Website name" value={form.name} onChange={set("name")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Brand name" value={form.brand_name} onChange={set("brand_name")} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Tagline" value={form.tagline} onChange={set("tagline")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="email" label="Contact email" value={form.contact_email} onChange={set("contact_email")} /></Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Locale" value={form.default_locale} onChange={set("default_locale")}>
                {LOCALES.map((locale) => <MenuItem key={locale.value} value={locale.value}>{locale.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Currency" value={form.default_currency} onChange={set("default_currency")}>
                {CURRENCIES.map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <MDTypography variant="h6" mb={2}>{realEstate ? "Brand assets and analytics" : "Brand assets, checkout and analytics"}</MDTypography>
          <BrandAssetFields
            values={form.assets}
            onChange={(field, value) => setForm((current) => ({ ...current, assets: { ...current.assets, [field]: value } }))}
            onError={onError}
            disabled={saving}
            showCheckout={!realEstate}
          />
          {hasSeep && (
            <>
              <Divider sx={{ my: 3 }} />
              <MDTypography variant="h6">Voice assistant</MDTypography>
              <FormControlLabel
                disabled={saving}
                control={<Switch checked={form.seep_enabled} onChange={(event) => setForm((current) => ({ ...current, seep_enabled: event.target.checked }))} />}
                label="Offer SEEP AI on this website"
              />
              <MDTypography variant="caption" color="text" display="block">
                {realEstate
                  ? "Visitors can talk to SEEP about your listings. SEEP can suggest an inspection; the visitor confirms it before anything is sent."
                  : "Visitors can talk to SEEP about your trips and add a departure to their cart. Payment is always their own step at checkout."}
              </MDTypography>
            </>
          )}
          <Divider sx={{ my: 3 }} />
          {realEstate ? (
            <>
              <MDTypography variant="h6">Home page</MDTypography>
              <MDTypography variant="body2" color="text" mb={2}>
                The top of your home page, above the property search. Leave a field blank to keep the built-in wording.
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}><TextField fullWidth label="Eyebrow" placeholder={form.brand_name || form.name} value={form.property.eyebrow} onChange={setCopy("eyebrow")} /></Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Headline" placeholder="Your trusted local real estate partner" value={form.property.title} onChange={setCopy("title")} helperText="The last word is shown in your accent colour." /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Introduction" value={form.property.copy} onChange={setCopy("copy")} /></Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <MDTypography variant="h6">About and contact</MDTypography>
              <MDTypography variant="body2" color="text" mb={2}>Shown in the About section, the contact panel and the footer.</MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="About your agency" value={form.property.description} onChange={setCopy("description")} /></Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Contact line" placeholder="03 9000 1000 · 12 Sydney Road, Brunswick" value={form.property.contact_line} onChange={setCopy("contact_line")} helperText="A phone number, address or opening hours." /></Grid>
              </Grid>
            </>
          ) : (
            <>
              <MDTypography variant="h6">Tours catalogue page</MDTypography>
              <MDTypography variant="body2" color="text" mb={2}>The heading shown above your published packages on the /tours page.</MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}><TextField fullWidth label="Eyebrow" value={form.pages.catalogue_eyebrow} onChange={setPage("catalogue_eyebrow")} /></Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Title" value={form.pages.catalogue_title} onChange={setPage("catalogue_title")} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Introduction" value={form.pages.catalogue_copy} onChange={setPage("catalogue_copy")} /></Grid>
              </Grid>
            </>
          )}
          <MDBox display="flex" gap={1} mt={3}>
            <MDButton color="info" disabled={!dirty || saving || !form.name.trim()} onClick={save}>{saving ? "Saving…" : "Save content"}</MDButton>
            {dirty && <MDButton variant="text" color="dark" disabled={saving} onClick={() => setForm(initial)}>Discard changes</MDButton>}
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
}

ContentTab.propTypes = {
  site: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  patch: PropTypes.func.isRequired,
  onOpenBuilder: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  realEstate: PropTypes.bool,
};

ContentTab.defaultProps = { realEstate: false };

export default ContentTab;
